import { mat3, vec3 } from "gl-matrix"
import { clamp, multiplyMat3 } from "@/core/utils/math"
import { adaptionMatrixBradford, adaptionMatrixBradfordInv } from "@/core/utils/color"
import { LookTableRGBGamma, LookTableRGBPrimaries } from "@/core/types"
// Color Space Conversion Matrices

// ProPhoto and XYZ matrices (D50)
export const matProphotoToXyz: mat3 = [
	0.7976719, 0.1351878, 0.0313396, 0.2880406, 0.7118695, 0.0000899, 0.0, 0.0, 0.8248898,
]

export const matXyzToProphoto: mat3 = [
	1.3459468, -0.2556025, -0.0511079, -0.5446045, 1.5081752, 0.0205265, 0.0, 0.0, 1.2122831,
]

export const matXyzToSrgb: mat3 = [
	3.1344866, -1.6174707, -0.4907276, -0.9787282, 1.9161073, 0.0334373, 0.071971, -0.2290228,
	1.4057805,
]

export const matSrgbToXyz: mat3 = [
	0.4360087, 0.3851511, 0.1430402, 0.2224659, 0.7169286, 0.0606055, 0.0139209, 0.0970801,
	0.713899,
]

export const matDisplayP32XYZ: mat3 = [
	0.515102, 0.291965, 0.157153, 0.241186, 0.692236, 0.0665819, -0.00104941, 0.0418818, 0.784378,
]

export function srgbToLinear(out: vec3, rgb: vec3) {
	out[0] = rgb[0] <= 0.04045 ? rgb[0] / 12.92 : Math.pow((rgb[0] + 0.055) / 1.055, 2.4)
	out[1] = rgb[1] <= 0.04045 ? rgb[1] / 12.92 : Math.pow((rgb[1] + 0.055) / 1.055, 2.4)
	out[2] = rgb[2] <= 0.04045 ? rgb[2] / 12.92 : Math.pow((rgb[2] + 0.055) / 1.055, 2.4)
	return out
}

export function clip(out: vec3, rgb: vec3, min: number, max: number) {
	out[0] = Math.min(Math.max(rgb[0], min), max)
	out[1] = Math.min(Math.max(rgb[1], min), max)
	out[2] = Math.min(Math.max(rgb[2], min), max)
	return out
}

export function linearToSrgb(out: vec3, rgb: vec3) {
	out[0] = rgb[0] <= 0.0031308 ? rgb[0] * 12.92 : 1.055 * Math.pow(rgb[0], 1.0 / 2.4) - 0.055
	out[1] = rgb[1] <= 0.0031308 ? rgb[1] * 12.92 : 1.055 * Math.pow(rgb[1], 1.0 / 2.4) - 0.055
	out[2] = rgb[2] <= 0.0031308 ? rgb[2] * 12.92 : 1.055 * Math.pow(rgb[2], 1.0 / 2.4) - 0.055
	return clip(out, out, 0, 1)
}

export function multiplyMat3xVec3(out: vec3, mat: mat3, vec: vec3): vec3 {
	out[0] = mat[0] * vec[0] + mat[1] * vec[1] + mat[2] * vec[2]
	out[1] = mat[3] * vec[0] + mat[4] * vec[1] + mat[5] * vec[2]
	out[2] = mat[6] * vec[0] + mat[7] * vec[1] + mat[8] * vec[2]

	return out
}

// Composite matrices
export const matProphotoToSrgb = multiplyMat3(matXyzToSrgb, matProphotoToXyz)
export const matSrgbToProphoto = multiplyMat3(matXyzToProphoto, matSrgbToXyz)
export const matDisplayP32Prophoto = multiplyMat3(matXyzToProphoto, matDisplayP32XYZ)
export const workingSpaceToXyz = matProphotoToXyz
export const xyzToWorkingSpace = matXyzToProphoto

export const workingSpaceToLms = multiplyMat3(adaptionMatrixBradford, workingSpaceToXyz)
export const lmsToWorkingSpace = multiplyMat3(xyzToWorkingSpace, adaptionMatrixBradfordInv)

const tempVec3 = vec3.create()

export function srgbToProphoto(out: vec3, rgb: vec3) {
	srgbToLinear(tempVec3, rgb)
	return multiplyMat3xVec3(out, matSrgbToProphoto, tempVec3)
}

export function displayP3ToProphoto(out: vec3, rgb: vec3) {
	srgbToLinear(tempVec3, rgb)
	return multiplyMat3xVec3(out, matDisplayP32Prophoto, tempVec3)
}

export function prophotoToSrgb(out: vec3, rgb: vec3) {
	multiplyMat3xVec3(tempVec3, matProphotoToSrgb, rgb)
	return linearToSrgb(out, tempVec3)
}

export function linearToGamma(out: vec3, rgb: vec3) {
	out[0] = Math.max(0, rgb[0])
	out[1] = Math.max(0, rgb[1])
	out[2] = Math.max(0, rgb[2])
	return linearToSrgb(out, out)
}

export function gammaToLinear(out: vec3, rgb: vec3) {
	out[0] = Math.max(0, rgb[0])
	out[1] = Math.max(0, rgb[1])
	out[2] = Math.max(0, rgb[2])
	return srgbToLinear(out, out)
}

export function prophotoToGamma2(out: vec3, rgb: vec3) {
	out[0] = Math.sqrt(Math.max(0, rgb[0]))
	out[1] = Math.sqrt(Math.max(0, rgb[1]))
	out[2] = Math.sqrt(Math.max(0, rgb[2]))
	return out
}

export function rgbToSat(rgb: vec3): number {
	const r = rgb[0]
	const g = rgb[1]
	const b = rgb[2]

	const max = Math.max(r, g, b)
	const min = Math.min(r, g, b)
	const delta = max - min

	const s = max === 0 ? 0 : delta / max
	return s
}

export function rgbToHsv(out: vec3, rgb: vec3): vec3 {
	const r = clamp(rgb[0], 0, 1)
	const g = clamp(rgb[1], 0, 1)
	const b = clamp(rgb[2], 0, 1)

	const max = Math.max(r, g, b)
	const min = Math.min(r, g, b)
	const delta = max - min

	let h = 0
	const s = max === 0 ? 0 : delta / max
	const v = max

	if (delta !== 0) {
		if (max === r) {
			h = (g - b) / delta + (g < b ? 6 : 0)
		} else if (max === g) {
			h = (b - r) / delta + 2
		} else {
			// max === b
			h = (r - g) / delta + 4
		}
		h /= 6
	}

	out[0] = h
	out[1] = s
	out[2] = v
	return out
}

export function rgbToHue(rgb: vec3): number {
	rgbToHsv(tempVec3, rgb)
	return tempVec3[0]
}

// input hsv in [0, 1] for this function; output RGB is always in [0, 1]
export function hsvToRgb(out: vec3, [h, s, v]: vec3): vec3 {
	let r: number, g: number, b: number
	h = clamp(h, 0, 1)
	s = clamp(s, 0, 1)
	v = clamp(v, 0, 1)

	const i = Math.floor(h * 6)
	const f = h * 6 - i
	const p = v * (1 - s)
	const q = v * (1 - f * s)
	const t = v * (1 - (1 - f) * s)

	switch ((i % 6) as 0 | 1 | 2 | 3 | 4 | 5) {
		case 0:
			r = v
			g = t
			b = p
			break
		case 1:
			r = q
			g = v
			b = p
			break
		case 2:
			r = p
			g = v
			b = t
			break
		case 3:
			r = p
			g = q
			b = v
			break
		case 4:
			r = t
			g = p
			b = v
			break
		case 5:
			r = v
			g = p
			b = q
			break
	}

	out[0] = r
	out[1] = g
	out[2] = b
	return out
}

// --- LUT primaries / gamma helpers (ported from looktable.glsl) ---

// Adobe RGB and XYZ (D65 white point)
// prettier-ignore
const matXyz2Adobe: mat3 = [
	 2.041369,  -0.5649464, -0.3446944,
	-0.969266,   1.8760108,  0.041556,
	 0.0134474, -0.1183897,  1.0154096,
]
// prettier-ignore
const matAdobe2Xyz: mat3 = [
	0.5767309, 0.185554,  0.1881852,
	0.2973769, 0.6273491, 0.0752741,
	0.0270343, 0.0706872, 0.9911085,
]

// Chromatic adaptation D50 <-> D65 (Bradford)
// prettier-ignore
const matD50D65: mat3 = [
	 0.9555766, -0.0230393,  0.0631636,
	-0.0282895,  1.0099416,  0.0210077,
	 0.0122982, -0.020483,   1.3299098,
]
// prettier-ignore
const matD65D50: mat3 = [
	 1.0478112,  0.0228866, -0.050127,
	 0.0295424,  0.9904844, -0.0170491,
	-0.0092345,  0.0150436,  0.7521316,
]

// Pre-computed composite matrices for LUT primaries conversion
// ProPhoto D50 -> sRGB: ProPhoto2XYZ * XYZ2sRGB  (reuses existing matrices)
export const matLutEncSrgb: mat3 = multiplyMat3(matXyzToSrgb, matProphotoToXyz)
// sRGB -> ProPhoto D50: sRGB2XYZ * XYZ2ProPhoto
export const matLutDecSrgb: mat3 = multiplyMat3(matXyzToProphoto, matSrgbToXyz)
// ProPhoto D50 -> Adobe D65: ProPhoto2XYZ * XYZ2Adobe * D50D65
export const matLutEncAdobe: mat3 = multiplyMat3(
	matD50D65,
	multiplyMat3(matXyz2Adobe, matProphotoToXyz),
)
// Adobe D65 -> ProPhoto D50: D65D50 * Adobe2XYZ * XYZ2ProPhoto
export const matLutDecAdobe: mat3 = multiplyMat3(
	matXyzToProphoto,
	multiplyMat3(matAdobe2Xyz, matD65D50),
)

export function lutPrimariesEncode(out: vec3, color: vec3, primariesType: number): vec3 {
	if (primariesType === LookTableRGBPrimaries.sRGB) {
		return multiplyMat3xVec3(out, matLutEncSrgb, color)
	} else if (primariesType === LookTableRGBPrimaries.adobe) {
		return multiplyMat3xVec3(out, matLutEncAdobe, color)
	}
	out[0] = color[0]
	out[1] = color[1]
	out[2] = color[2]
	return out
}

export function lutPrimariesDecode(out: vec3, color: vec3, primariesType: number): vec3 {
	if (primariesType === LookTableRGBPrimaries.sRGB) {
		return multiplyMat3xVec3(out, matLutDecSrgb, color)
	} else if (primariesType === LookTableRGBPrimaries.adobe) {
		return multiplyMat3xVec3(out, matLutDecAdobe, color)
	}
	out[0] = color[0]
	out[1] = color[1]
	out[2] = color[2]
	return out
}

export function lutGammaEncode(out: vec3, color: vec3, gammaType: number): vec3 {
	if (gammaType === LookTableRGBGamma.sRGB) {
		return linearToSrgb(out, color)
	} else if (gammaType === LookTableRGBGamma.gamma1_8) {
		out[0] = Math.pow(color[0], 1.0 / 1.8)
		out[1] = Math.pow(color[1], 1.0 / 1.8)
		out[2] = Math.pow(color[2], 1.0 / 1.8)
		return out
	} else if (gammaType === LookTableRGBGamma.gamma2_2) {
		out[0] = Math.pow(color[0], 1.0 / 2.2)
		out[1] = Math.pow(color[1], 1.0 / 2.2)
		out[2] = Math.pow(color[2], 1.0 / 2.2)
		return out
	} else if (gammaType === LookTableRGBGamma.rec2020) {
		out[0] = Math.pow(color[0], 1.0 / 2.4)
		out[1] = Math.pow(color[1], 1.0 / 2.4)
		out[2] = Math.pow(color[2], 1.0 / 2.4)
		return out
	}
	out[0] = color[0]
	out[1] = color[1]
	out[2] = color[2]
	return out
}

export function lutGammaDecode(out: vec3, color: vec3, gammaType: number): vec3 {
	if (gammaType === LookTableRGBGamma.sRGB) {
		return srgbToLinear(out, color)
	} else if (gammaType === LookTableRGBGamma.gamma1_8) {
		out[0] = Math.pow(color[0], 1.8)
		out[1] = Math.pow(color[1], 1.8)
		out[2] = Math.pow(color[2], 1.8)
		return out
	} else if (gammaType === LookTableRGBGamma.gamma2_2) {
		out[0] = Math.pow(color[0], 2.2)
		out[1] = Math.pow(color[1], 2.2)
		out[2] = Math.pow(color[2], 2.2)
		return out
	} else if (gammaType === LookTableRGBGamma.rec2020) {
		out[0] = Math.pow(color[0], 2.4)
		out[1] = Math.pow(color[1], 2.4)
		out[2] = Math.pow(color[2], 2.4)
		return out
	}
	out[0] = color[0]
	out[1] = color[1]
	out[2] = color[2]
	return out
}

/**
 * Apply a color transform function to each pixel in an image
 * @param image Float32Array of shape (H*W*3)
 * @param transform Function that takes a vec3 and returns a vec3
 * @returns Transformed image as Float32Array
 */
export function applyTransform(
	image: Float32Array,
	transform: (out: vec3, rgb: vec3) => vec3,
): Float32Array {
	const result = new Float32Array(image.length)
	const pixel: vec3 = [0, 0, 0]
	const out: vec3 = [0, 0, 0]

	for (let i = 0; i < image.length; i += 3) {
		// Get RGB values
		pixel[0] = image[i] as number
		pixel[1] = image[i + 1] as number
		pixel[2] = image[i + 2] as number

		// Apply transform
		transform(out, pixel)

		// Store result
		result[i] = out[0]
		result[i + 1] = out[1]
		result[i + 2] = out[2]
	}

	return result
}

export function applyTransformToOneDimension(
	image: Float32Array,
	transform: (rgb: vec3) => number,
): Float32Array {
	const result = new Float32Array(image.length / 3)
	const pixel: vec3 = [0, 0, 0]

	for (let i = 0; i < result.length; i += 1) {
		// Get RGB values
		const j = i * 3
		pixel[0] = image[j] as number
		pixel[1] = image[j + 1] as number
		pixel[2] = image[j + 2] as number

		// Apply transform
		const transformed = transform(pixel)

		// Store result
		result[i] = transformed
	}

	return result
}
